// =============================================================================
// SimSense Platform — Coach Dashboard (B2B-002)
// =============================================================================
// Coaches can create/edit their public coaching profile and define service
// offerings (async review, live session, package, etc.)
// =============================================================================

import { useEffect, useState } from "react";
import { api } from "../utils/api";
import { useAuthStore } from "../state/authStore";

const SERVICE_TYPES = [
  { value: "async_review", label: "Async Telemetry Review" },
  { value: "live_session", label: "Live Coaching Session" },
  { value: "package", label: "Multi-Session Package" },
  { value: "office_hours", label: "Office Hours" },
  { value: "bootcamp", label: "Bootcamp / Cohort" },
];

const DISCIPLINES = ["Oval", "Road", "Dirt Oval", "Dirt Road", "Sports Car", "Open Wheel", "Stock Car", "GT"];

export function CoachDashboard() {
  const { user } = useAuthStore();
  const [, setProfile] = useState<any>(null);
  const [services, setServices] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingService, setEditingService] = useState<any>(null);
  const [isNew, setIsNew] = useState(false);

  // Profile form state
  const [form, setForm] = useState({
    slug: "",
    brand_name: "",
    headline: "",
    bio: "",
    specialties: [] as string[],
    supported_disciplines: [] as string[],
    base_currency: "usd",
    hourly_rate_cents: 0,
    async_review_price_cents: 0,
    response_sla_hours: 24,
    is_public: false,
  });

  const [newSpecialty, setNewSpecialty] = useState("");

  useEffect(() => {
    async function load() {
      try {
        const p = await api<any>("/api/coach-platform/me/profile");
        setProfile(p);
        setForm({
          slug: p.slug || "",
          brand_name: p.brandName || p.brand_name || "",
          headline: p.headline || "",
          bio: p.bio || "",
          specialties: p.specialties || [],
          supported_disciplines: p.supportedDisciplines || p.supported_disciplines || [],
          base_currency: p.baseCurrency || p.base_currency || "usd",
          hourly_rate_cents: p.hourlyRateCents || p.hourly_rate_cents || 0,
          async_review_price_cents: p.asyncReviewPriceCents || p.async_review_price_cents || 0,
          response_sla_hours: p.responseSlaHours || p.response_sla_hours || 24,
          is_public: p.isPublic ?? p.is_public ?? false,
        });
      } catch {
        // No profile yet — show create form
        setIsNew(true);
        setForm(f => ({ ...f, slug: user?.displayName?.toLowerCase().replace(/\s+/g, "-") || "" }));
      }

      try {
        const s = await api<any[]>("/api/coach-platform/me/services");
        setServices(s);
      } catch {
        // No services yet
      }

      setLoading(false);
    }
    load();
  }, [user]);

  const saveProfile = async () => {
    setSaving(true);
    try {
      const updated = await api<any>("/api/coach-platform/me/profile", {
        method: "PUT",
        body: JSON.stringify(form),
      });
      setProfile(updated);
      setIsNew(false);
    } catch (err: any) {
      alert(err?.message || "Failed to save profile");
    } finally {
      setSaving(false);
    }
  };

  const saveService = async (svc: any) => {
    setSaving(true);
    try {
      if (svc.id) {
        const updated = await api<any>(`/api/coach-platform/me/services/${svc.id}`, {
          method: "PATCH",
          body: JSON.stringify(svc),
        });
        setServices(prev => prev.map(s => s.id === updated.id ? updated : s));
      } else {
        const created = await api<any>("/api/coach-platform/me/services", {
          method: "POST",
          body: JSON.stringify(svc),
        });
        setServices(prev => [...prev, created]);
      }
      setEditingService(null);
    } catch (err: any) {
      alert(err?.message || "Failed to save service");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div style={{ padding: "40px", textAlign: "center" }}>Loading coach dashboard...</div>;

  return (
    <div style={{ maxWidth: "900px", margin: "0 auto", display: "flex", flexDirection: "column", gap: "24px" }}>
      <div>
        <h2 style={{ marginBottom: "4px" }}>Coach Dashboard</h2>
        <p style={{ color: "var(--text2)", fontSize: "13px" }}>
          {isNew ? "Set up your coaching profile to start offering services." : "Manage your coaching profile and service catalog."}
        </p>
      </div>

      {/* Profile Section */}
      <section style={{ background: "var(--card)", padding: "24px", borderRadius: "var(--radius)", border: "1px solid var(--border)" }}>
        <h3 style={{ fontSize: "16px", marginBottom: "16px" }}>Coaching Profile</h3>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
          <div>
            <label style={labelStyle}>Profile URL Slug</label>
            <input value={form.slug} onChange={e => setForm(f => ({ ...f, slug: e.target.value }))}
              placeholder="your-coach-name" style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Brand / Display Name</label>
            <input value={form.brand_name} onChange={e => setForm(f => ({ ...f, brand_name: e.target.value }))}
              placeholder="Your Coaching Brand" style={inputStyle} />
          </div>
          <div style={{ gridColumn: "1 / -1" }}>
            <label style={labelStyle}>Headline</label>
            <input value={form.headline} onChange={e => setForm(f => ({ ...f, headline: e.target.value }))}
              placeholder="e.g. NASCAR oval specialist — 10+ years of iRacing coaching" style={inputStyle} />
          </div>
          <div style={{ gridColumn: "1 / -1" }}>
            <label style={labelStyle}>Bio</label>
            <textarea value={form.bio} onChange={e => setForm(f => ({ ...f, bio: e.target.value }))}
              placeholder="Tell students about your experience, coaching style, and what they'll learn..."
              rows={4} style={{ ...inputStyle, resize: "vertical" }} />
          </div>

          {/* Disciplines */}
          <div style={{ gridColumn: "1 / -1" }}>
            <label style={labelStyle}>Disciplines</label>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
              {DISCIPLINES.map(d => (
                <button key={d} onClick={() => {
                  setForm(f => ({
                    ...f,
                    supported_disciplines: f.supported_disciplines.includes(d)
                      ? f.supported_disciplines.filter(x => x !== d)
                      : [...f.supported_disciplines, d],
                  }));
                }} style={{
                  padding: "6px 14px", borderRadius: "20px", fontSize: "12px", fontWeight: 600, cursor: "pointer",
                  background: form.supported_disciplines.includes(d) ? "var(--accent)" : "rgba(255,255,255,0.05)",
                  color: form.supported_disciplines.includes(d) ? "#fff" : "var(--text2)",
                  border: `1px solid ${form.supported_disciplines.includes(d) ? "var(--accent)" : "var(--border)"}`,
                }}>{d}</button>
              ))}
            </div>
          </div>

          {/* Specialties */}
          <div style={{ gridColumn: "1 / -1" }}>
            <label style={labelStyle}>Specialties (tags)</label>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", marginBottom: "8px" }}>
              {form.specialties.map((s, i) => (
                <span key={i} style={{ padding: "4px 10px", background: "rgba(59,130,246,0.15)", borderRadius: "12px", fontSize: "11px", color: "var(--accent)", display: "flex", alignItems: "center", gap: "6px" }}>
                  {s}
                  <button onClick={() => setForm(f => ({ ...f, specialties: f.specialties.filter((_, j) => j !== i) }))}
                    style={{ background: "none", border: "none", color: "var(--text3)", cursor: "pointer", fontSize: "12px" }}>x</button>
                </span>
              ))}
            </div>
            <div style={{ display: "flex", gap: "8px" }}>
              <input value={newSpecialty} onChange={e => setNewSpecialty(e.target.value)}
                placeholder="e.g. Oval setup, Brake technique" style={{ ...inputStyle, flex: 1 }}
                onKeyDown={e => {
                  if (e.key === "Enter" && newSpecialty.trim()) {
                    setForm(f => ({ ...f, specialties: [...f.specialties, newSpecialty.trim()] }));
                    setNewSpecialty("");
                  }
                }} />
              <button onClick={() => {
                if (newSpecialty.trim()) {
                  setForm(f => ({ ...f, specialties: [...f.specialties, newSpecialty.trim()] }));
                  setNewSpecialty("");
                }
              }} style={btnStyle}>Add</button>
            </div>
          </div>

          {/* Pricing */}
          <div>
            <label style={labelStyle}>Async Review Price</label>
            <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
              <span style={{ color: "var(--text3)", fontSize: "14px" }}>$</span>
              <input type="number" value={(form.async_review_price_cents / 100).toFixed(2)}
                onChange={e => setForm(f => ({ ...f, async_review_price_cents: Math.round(parseFloat(e.target.value || "0") * 100) }))}
                style={{ ...inputStyle, flex: 1 }} step="0.01" min="0" />
            </div>
          </div>
          <div>
            <label style={labelStyle}>Hourly Rate</label>
            <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
              <span style={{ color: "var(--text3)", fontSize: "14px" }}>$</span>
              <input type="number" value={(form.hourly_rate_cents / 100).toFixed(2)}
                onChange={e => setForm(f => ({ ...f, hourly_rate_cents: Math.round(parseFloat(e.target.value || "0") * 100) }))}
                style={{ ...inputStyle, flex: 1 }} step="0.01" min="0" />
            </div>
          </div>
          <div>
            <label style={labelStyle}>Response SLA (hours)</label>
            <input type="number" value={form.response_sla_hours}
              onChange={e => setForm(f => ({ ...f, response_sla_hours: parseInt(e.target.value) || 24 }))}
              style={inputStyle} min="1" />
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "12px", paddingTop: "20px" }}>
            <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer" }}>
              <input type="checkbox" checked={form.is_public}
                onChange={e => setForm(f => ({ ...f, is_public: e.target.checked }))}
                style={{ width: "18px", height: "18px", accentColor: "var(--accent)" }} />
              <span style={{ fontWeight: 600, fontSize: "13px" }}>Publish Profile</span>
            </label>
          </div>
        </div>

        <div style={{ marginTop: "20px" }}>
          <button onClick={saveProfile} disabled={saving} style={{
            ...btnStyle, background: "var(--accent)", padding: "10px 24px",
            opacity: saving ? 0.6 : 1, cursor: saving ? "not-allowed" : "pointer",
          }}>{saving ? "Saving..." : isNew ? "Create Profile" : "Save Profile"}</button>
        </div>
      </section>

      {/* Services Section */}
      <section style={{ background: "var(--card)", padding: "24px", borderRadius: "var(--radius)", border: "1px solid var(--border)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
          <h3 style={{ fontSize: "16px" }}>Service Catalog</h3>
          <button onClick={() => setEditingService({
            service_type: "async_review", title: "", description: "", price_cents: 0,
            currency: "usd", duration_minutes: 60, delivery_sla_hours: 48,
          })} style={btnStyle}>+ Add Service</button>
        </div>

        {services.length === 0 && !editingService && (
          <div style={{ padding: "32px", textAlign: "center", color: "var(--text3)", border: "1px dashed var(--border)", borderRadius: "8px" }}>
            No services defined yet. Add your first coaching service to start accepting students.
          </div>
        )}

        <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          {services.map(svc => (
            <div key={svc.id} style={{ padding: "16px", background: "rgba(255,255,255,0.03)", borderRadius: "8px", border: "1px solid var(--border)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: "14px", marginBottom: "4px" }}>{svc.title}</div>
                <div style={{ fontSize: "12px", color: "var(--text2)", display: "flex", gap: "16px" }}>
                  <span>{SERVICE_TYPES.find(t => t.value === (svc.serviceType || svc.service_type))?.label || svc.service_type}</span>
                  <span style={{ color: "var(--accent)", fontWeight: 600 }}>${((svc.priceCents || svc.price_cents || 0) / 100).toFixed(2)}</span>
                  {(svc.durationMinutes || svc.duration_minutes) && <span>{svc.durationMinutes || svc.duration_minutes} min</span>}
                </div>
              </div>
              <button onClick={() => setEditingService({
                ...svc,
                service_type: svc.serviceType || svc.service_type,
                price_cents: svc.priceCents || svc.price_cents || 0,
                duration_minutes: svc.durationMinutes || svc.duration_minutes || 60,
                delivery_sla_hours: svc.deliverySlaHours || svc.delivery_sla_hours || 48,
              })} style={{ ...btnStyle, fontSize: "11px" }}>Edit</button>
            </div>
          ))}
        </div>

        {/* Service Editor */}
        {editingService && (
          <div style={{ marginTop: "16px", padding: "20px", background: "rgba(59,130,246,0.05)", border: "1px solid rgba(59,130,246,0.2)", borderRadius: "8px" }}>
            <h4 style={{ fontSize: "14px", marginBottom: "12px" }}>{editingService.id ? "Edit Service" : "New Service"}</h4>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
              <div>
                <label style={labelStyle}>Service Type</label>
                <select value={editingService.service_type} onChange={e => setEditingService((s: any) => ({ ...s, service_type: e.target.value }))}
                  style={inputStyle}>
                  {SERVICE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Title</label>
                <input value={editingService.title || ""} onChange={e => setEditingService((s: any) => ({ ...s, title: e.target.value }))}
                  placeholder="e.g. Full Telemetry Review" style={inputStyle} />
              </div>
              <div style={{ gridColumn: "1 / -1" }}>
                <label style={labelStyle}>Description</label>
                <textarea value={editingService.description || ""} onChange={e => setEditingService((s: any) => ({ ...s, description: e.target.value }))}
                  placeholder="What's included in this service?" rows={3} style={{ ...inputStyle, resize: "vertical" }} />
              </div>
              <div>
                <label style={labelStyle}>Price</label>
                <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                  <span style={{ color: "var(--text3)" }}>$</span>
                  <input type="number" value={(editingService.price_cents / 100).toFixed(2)}
                    onChange={e => setEditingService((s: any) => ({ ...s, price_cents: Math.round(parseFloat(e.target.value || "0") * 100) }))}
                    style={{ ...inputStyle, flex: 1 }} step="0.01" min="0" />
                </div>
              </div>
              <div>
                <label style={labelStyle}>Duration (minutes)</label>
                <input type="number" value={editingService.duration_minutes || ""}
                  onChange={e => setEditingService((s: any) => ({ ...s, duration_minutes: parseInt(e.target.value) || 0 }))}
                  style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Delivery SLA (hours)</label>
                <input type="number" value={editingService.delivery_sla_hours || ""}
                  onChange={e => setEditingService((s: any) => ({ ...s, delivery_sla_hours: parseInt(e.target.value) || 0 }))}
                  style={inputStyle} />
              </div>
            </div>
            <div style={{ display: "flex", gap: "8px", marginTop: "16px" }}>
              <button onClick={() => saveService(editingService)} disabled={saving} style={{
                ...btnStyle, background: "var(--accent)", padding: "8px 20px",
                opacity: saving ? 0.6 : 1,
              }}>{saving ? "Saving..." : "Save Service"}</button>
              <button onClick={() => setEditingService(null)} style={{ ...btnStyle, background: "transparent", border: "1px solid var(--border)" }}>Cancel</button>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}

const labelStyle: React.CSSProperties = {
  display: "block", fontSize: "11px", fontWeight: 600, color: "var(--text2)",
  textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "4px",
};

const inputStyle: React.CSSProperties = {
  width: "100%", padding: "8px 12px", background: "var(--bg)", border: "1px solid var(--border)",
  borderRadius: "6px", color: "#fff", fontSize: "13px",
};

const btnStyle: React.CSSProperties = {
  padding: "6px 14px", background: "rgba(255,255,255,0.08)", border: "none",
  borderRadius: "6px", color: "var(--text)", fontSize: "12px", fontWeight: 600, cursor: "pointer",
};
